import { brandApi } from '@/api/account-management/brandApi';
import { locationGroupApi } from '@/api/account-management/locationGroupApi';
import { type Location } from '@/models/account-management/Location';
import { type LocationGroup } from '@/models/account-management/LocationGroup';
import Checkbox from '@mui/material/Checkbox';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import ListItemText from '@mui/material/ListItemText';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useLocalizedStrings } from '../../../localization/LocalizedStringsProvider';
import { useToast } from '../../common/toast/ToastProvider';
import ReadOnlyFieldset from '../../common/dialog/ReadOnlyFieldset';

interface Props {
    open: boolean;
    brandId: string;
    group: LocationGroup | null; // null = create
    // View-only: the grid opens this with an eye rather than a pencil for read access alone.
    readOnly?: boolean;
    onClose: () => void;
}

const LocationGroupDialog = ({ open, brandId, group, readOnly, onClose }: Props) => {
    const qc = useQueryClient();
    const toast = useToast();
    const strings = useLocalizedStrings();

    const [name, setName] = useState('');
    const [description, setDescription] = useState('');
    const [priority, setPriority] = useState(0);
    const [selectedLocationIds, setSelectedLocationIds] = useState<string[]>([]);

    useEffect(() => {
        if (!open) return;
        setName(group?.name ?? '');
        setDescription(group?.description ?? '');
        setPriority(group?.priority ?? 0);
        setSelectedLocationIds(group?.members?.map((m) => m.locationId) ?? []);
    }, [open, group]);

    const { data: locations } = useQuery<Location[]>({
        queryKey: ['locations', brandId ?? ''],
        enabled: open && !!brandId,
        queryFn: () => brandApi.listLocations(brandId),
        staleTime: Infinity,
    });

    const { mutateAsync: save, isPending } = useMutation({
        mutationFn: async () => {
            const payload: LocationGroup = {
                id: group?.id,
                brandId,
                name: name.trim(),
                description: description.trim() ? description.trim() : undefined,
                priority,
                members: selectedLocationIds.map((locationId) => ({ locationId })),
            };
            await locationGroupApi.save(payload);
        },
        onSuccess: async () => {
            toast({ severity: 'success', message: strings.locationGroupSaved });
            await qc.invalidateQueries({ queryKey: ['location-groups', brandId] });
            onClose();
        },
        onError: () => toast({ severity: 'error', message: strings.locationGroupSaveError }),
    });

    const canSave = name.trim().length > 0;

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
            <DialogTitle>{group ? strings.locationGroupEdit : strings.locationGroupNew}</DialogTitle>
            <DialogContent>
                <ReadOnlyFieldset readOnly={readOnly}>
                <Stack spacing={2} sx={{ mt: 1 }}>
                    <TextField label={strings.name} value={name} onChange={(e) => setName(e.target.value)} fullWidth autoFocus />
                    <TextField label={strings.description} value={description} onChange={(e) => setDescription(e.target.value)} fullWidth multiline minRows={2} />
                    <TextField label={strings.priority} value={priority === 0 ? '' : priority} onChange={(e) => setPriority(Number(e.target.value) || 0)} type="number" sx={{ maxWidth: 160 }} />

                    <FormControl fullWidth>
                        <InputLabel id="group-locations-label">{strings.locations}</InputLabel>
                        <Select
                            labelId="group-locations-label"
                            label={strings.locations}
                            multiple
                            value={selectedLocationIds}
                            onChange={(e) => setSelectedLocationIds(typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value)}
                            renderValue={(selected) =>
                                (locations ?? []).filter((l) => selected.includes(l.id!)).map((l) => l.name).join(', ')
                            }
                        >
                            {(locations ?? []).map((l) => (
                                <MenuItem key={l.id} value={l.id}>
                                    <Checkbox checked={selectedLocationIds.includes(l.id!)} />
                                    <ListItemText primary={l.name} />
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Stack>
            </ReadOnlyFieldset>
            </DialogContent>
            <DialogActions>
                <Button variant="outlined" onClick={onClose}>{readOnly ? strings.close : strings.cancel}</Button>
                {!readOnly && <Button variant="contained" loading={isPending} disabled={!canSave} onClick={() => save()}>{strings.save}</Button>}
            </DialogActions>
        </Dialog>
    );
};

export default LocationGroupDialog;
